import React from "react";
import * as Sentry from "@sentry/react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router";
import { scrubEvent, scrubBreadcrumb, scrubLog } from "../lib/sentry-scrub";

const isProd =
  import.meta.env.PROD && import.meta.env.VITE_SENTRY_ENV === "production";

/**
 * Telemetry volume is a billing lever, not just a config knob. The Sentry Team
 * plan reserves 5M spans/month and only 50 session replays/month across EVERY
 * Palate app combined, so a 1.0 sample rate outside production drains a shared
 * budget. Sampling at 1.0 off-prod burned the entire monthly pay-as-you-go
 * allowance in the Jul-Aug 2026 cycle.
 *
 * Defaults are deliberately thin. Each is env-overridable (0..1) so a specific
 * investigation can turn the dial up for one app without a code change.
 */
function sampleRate(raw: string | undefined, fallback: number): number {
  if (raw === undefined || raw.trim() === "") return fallback;
  const parsed = Number(raw);
  // Reject junk rather than silently disabling telemetry or removing the cap.
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) return fallback;
  return parsed;
}

const TRACES_SAMPLE_RATE = sampleRate(
  import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
  isProd ? 0.1 : 0.01,
);

/**
 * Session replay is off by default: 50/month across all six apps is not an
 * always-on budget. Set VITE_SENTRY_REPLAY_SESSION_RATE for one app while
 * actively debugging, then put it back.
 */
const REPLAY_SESSION_SAMPLE_RATE = sampleRate(
  import.meta.env.VITE_SENTRY_REPLAY_SESSION_RATE,
  0,
);

/** Error replays are the valuable ones, but they draw on the same 50. */
const REPLAY_ERROR_SAMPLE_RATE = sampleRate(
  import.meta.env.VITE_SENTRY_REPLAY_ERROR_RATE,
  isProd ? 0.1 : 0,
);

/**
 * Module-level flag toggled by logger.ts `forceSend` to let demo events
 * bypass the development `beforeSend` gate.  Sentry v10 applies capture-context
 * tags to a scope clone, so they aren't visible on `event.tags` inside
 * `beforeSend`. This flag is the reliable alternative.
 */
let _allowNextDemoEvent = false;
export function allowDemoEvent() {
  _allowNextDemoEvent = true;
}

if (typeof window !== "undefined") {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENV ?? "staging",
    // Set at build time from VITE_GIT_SHA; vite.config normalizes CF_PAGES_COMMIT_SHA etc.
    release: import.meta.env.VITE_GIT_SHA || undefined,
    debug: false,
    // Ops/PII hardening: identity is attached as an opaque setUser({ id }) in
    // app/lib/logger.ts — never email/phone/IP (backend #1874 review, admin#936).
    sendDefaultPii: false,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        // Replays bypass beforeSend, so the scrubber never sees them: mask all
        // rendered text (guest names, phones, amounts) and never record API
        // request/response bodies (login/refresh bodies carry tokens) — admin#936.
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
        networkDetailAllowUrls: [/^https:\/\/api\.palate\.pro/],
        networkCaptureBodies: false,
      }),
    ],
    tracesSampleRate: TRACES_SAMPLE_RATE,
    replaysSessionSampleRate: REPLAY_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: REPLAY_ERROR_SAMPLE_RATE,

    enableLogs: true,

    initialScope: { tags: { service: 'admin-app' } },

    beforeBreadcrumb(breadcrumb) {
      return scrubBreadcrumb(breadcrumb);
    },

    beforeSendLog(log) {
      return scrubLog(log);
    },

    beforeSend(event) {
      if (import.meta.env.DEV) {
        if (_allowNextDemoEvent) {
          _allowNextDemoEvent = false;
          event.environment = "staging";
        } else {
          return null;
        }
      }

      const userAgent =
        event.request?.headers?.["User-Agent"] ??
        event.request?.headers?.["user-agent"] ??
        (typeof navigator !== "undefined" ? navigator.userAgent : "");
      if (userAgent.toLowerCase().includes("bot")) {
        return null;
      }

      // Client-side router: SDK sometimes attaches the document URL or a stale
      // request URL. Pin Issues "URL" to the location at capture time.
      // `transaction` often comes from the active trace (parent layout, e.g.
      // `/dashboard`); Slack/issue titles use it — align with the real path.
      if (
        event.type !== "transaction" &&
        typeof window !== "undefined" &&
        window.location?.href
      ) {
        event.request = { ...event.request, url: window.location.href };
        if (window.location.pathname) {
          event.transaction = window.location.pathname;
        }
      }

      return scrubEvent(event);
    },

    // Transaction events (spans/traces) do NOT go through beforeSend — the
    // SDK routes them here instead. Spans can carry the same query-string
    // secrets as request URLs and breadcrumbs (e.g. an http.url span
    // attribute), so this needs its own scrub pass.
    beforeSendTransaction(event) {
      return scrubEvent(event);
    },
  });
}

export {Sentry}
